// @ts-strict-ignore
import React, { useState } from 'react'
import { Doughnut } from 'react-chartjs-2'
import { mortgageCalculation } from './calculation'
import Trustpilot from './mojo-trustpilot'
import { Input, Button, Icon } from '../../../../atoms'
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js'
import getWindow from '@u/get-window'

ChartJS.register(ArcElement, Tooltip, Legend)

type LoanDetails = {
  estPropertyBudget: number
  borrowing: number
  loanToValue: number | string
  deposit: number
}

const CLICKOUT_LINK =
  'https://uswitch.mojomortgages.com/get-started?utm_source=partner&utm_medium=uswitch&utm_term=g68gqKGVTVyFSa8OCFVs6A&utm_campaign=product_page'

const ResultsScreen = ({ loanDetails, setShowMortgageGauge }: { loanDetails: LoanDetails }) => {
  const data = {
    datasets: [
      {
        label: 'Poll',
        data: [loanDetails.deposit, loanDetails.borrowing],
        backgroundColor: ['#000000', '#AC96DE'],
        borderColor: ['#000000', '#AC96DE'],
        circumference: 180,
        rotation: 270,
      },
    ],
  }

  const options = {
    cutout: 80,
  }

  return (
    <div className='MortgageAffordabilityCalculator__inner-box'>
      <div className='MortgageAffordabilityCalculator__donut-box'>
        <Doughnut data={data} options={options} />
        <div className='MortgageAffordabilityCalculator__loan-details-container'>
          <div className='MortgageAffordabilityCalculator__loan-details-value'>
            {loanDetails.loanToValue}
          </div>
          <div>loan to value</div>
        </div>
      </div>
      <div className='MortgageAffordabilityCalculator__budget-box'>
        <div className='MortgageAffordabilityCalculator__title'>Your estimated property budget</div>
        <div className='MortgageAffordabilityCalculator__property-figure'>
          £{loanDetails.estPropertyBudget.toLocaleString('en-GB')}
        </div>
      </div>
      <div className='MortgageAffordabilityCalculator__center'>
        <div className='MortgageAffordabilityCalculator__center-figures-container'>
          <div className='MortgageAffordabilityCalculator__center-figures'>
            <div className='MortgageAffordabilityCalculator__center-text'>Deposit</div>£
            {loanDetails.deposit.toLocaleString('en-GB')}
            <div className='MortgageAffordabilityCalculator__dot MortgageAffordabilityCalculator__dot--black'></div>
          </div>
          <div className='MortgageAffordabilityCalculator__center-figures'>
            <div className='MortgageAffordabilityCalculator__center-text'>Borrowing</div>£
            {loanDetails.borrowing.toLocaleString('en-GB')}
            <div className='MortgageAffordabilityCalculator__dot MortgageAffordabilityCalculator__dot--blue'></div>
          </div>
        </div>
      </div>
      <div className='MortgageAffordabilityCalculator__center-text-wording'>
        <p>Remember the above amount is an estimate of what you can borrow.</p>
        <p>To find out exactly what you could get, speak to a mortgage adviser.</p>
      </div>
      <div className='MortgageAffordabilityCalculator__button-advice'>
        <Button
          style={{ margin: '0, 20px, 0, auto' }}
          variant='primary'
          type='submit'
          href={CLICKOUT_LINK}
        >
          Get free mortgage advice
        </Button>
      </div>
      <div className='MortgageAffordabilityCalculator__button-recalc'>
        <Button
          style={{ margin: '0 auto', display: 'inline-block' }}
          variant='secondary'
          type='button'
          onClick={() => {
            setShowMortgageGauge(false)
            const window = getWindow()
            window.scrollTo(0, 0)
          }}
        >
          Recalculate
        </Button>
      </div>
      <div className='MortgageAffordabilityCalculator__mojo-logo'>
        <Icon glyph='powered-by-mojo' />
      </div>
      <div className='MortgageAffordabilityCalculator__trustpilot'>
        <Trustpilot theme='light' />
      </div>
    </div>
  )
}

interface ApplicantDetailsProps {
  annualIncome: number
  otherIncome: number
  updateApplicant: (annualIncome: number, otherIncome: number) => void
  firstApplicant: boolean
  error: boolean
}
const ApplicantDetails = ({
  annualIncome,
  otherIncome,
  firstApplicant,
  error,
  updateApplicant,
}: ApplicantDetailsProps) => {
  const [showOtherIncome, setShowOtherIncome] = useState<boolean>(false)

  const handleToggleOtherIncome = () => setShowOtherIncome(!showOtherIncome)

  // Now, whenever you update these state variables, call 'updateApplicant'
  const setAnnualIncome = (newValue: number) => {
    updateApplicant(newValue, otherIncome)
  }

  const setOtherIncome = (newValue: number) => {
    updateApplicant(annualIncome, newValue)
  }

  return (
    <React.Fragment>
      <div className='MortgageAffordabilityCalculator__question-container'>
        <label htmlFor='annualIncome'>
          What is {firstApplicant ? 'your' : 'their'} annual salary before tax?
        </label>
        <Input
          name='annualIncome'
          value={annualIncome}
          onChange={(e) => setAnnualIncome(e.target.value)}
          prefix='£'
          format='thousands'
          error={error}
        />
        {error && (
          <div className='error'>
            Annual salary + other guaranteed income must be more than £15,000
          </div>
        )}
      </div>
      {showOtherIncome || !firstApplicant ? (
        <div className='MortgageAffordabilityCalculator__question-container'>
          <label htmlFor='otherIncome'>
            Do {firstApplicant ? 'you' : 'they'} have any other guaranteed income?
          </label>
          <Input
            name='otherIncome'
            value={otherIncome}
            onChange={(e) => setOtherIncome(e.target.value)}
            prefix='£'
            format='thousands'
            error={error}
          />
          {error && (
            <div className='MortgageAffordabilityCalculator__error'>
              Annual salary + other guaranteed income must be more than £15,000
            </div>
          )}
        </div>
      ) : (
        <div className='MortgageAffordabilityCalculator__button-padding'>
          <Button variant='secondary' type='button' onClick={() => handleToggleOtherIncome()}>
            Add additional income?
          </Button>
        </div>
      )}
    </React.Fragment>
  )
}

class Applicant {
  annualIncome = 15000
  otherIncome = 0
  errorIncome = false
  id = Math.floor(Math.random() * 100)
}

const startApplicant = new Applicant()

const ApplicantForm = ({ onSubmit }: { onSubmit: (calculation) => {} }) => {
  const [applicants, setApplicants] = useState<Record<number, Applicant>>({
    [startApplicant.id]: startApplicant,
  })
  const [deposit, setDeposit] = useState(5000)

  const errorRule = (applicant: Applicant) => applicant.annualIncome + applicant.otherIncome < 15000

  const totalIncome = React.useMemo(
    () => Object.values(applicants).reduce((total, applicant) => total + applicant.annualIncome, 0),
    [applicants],
  )
  const totalOtherIncome = React.useMemo(
    () => Object.values(applicants).reduce((total, applicant) => total + applicant.otherIncome, 0),
    [applicants],
  )
  const applicantErrors = React.useMemo(
    () => Object.values(applicants).some((applicant) => applicant.errorIncome),
    [applicants],
  )

  const handleSubmit = (e: any) => {
    e.preventDefault()

    const calculation = mortgageCalculation({
      annualIncome: totalIncome,
      otherIncome: totalOtherIncome,
      deposit,
    })

    onSubmit(calculation)
  }

  const handleNewApplicant = () => {
    if (Object.keys(applicants).length === 1) {
      const newApplicant = new Applicant()
      setApplicants((oldState) => ({
        ...oldState,
        [newApplicant.id]: newApplicant,
      }))
    }
  }

  const handleUpdateApplicant = (annualIncome: number, otherIncome: number, id: number) => {
    setApplicants((oldState) => ({
      ...oldState,
      [id]: {
        id,
        annualIncome,
        otherIncome,
      },
    }))
  }

  const errorDeposit = deposit < 4999

  return (
    <div className='MortgageAffordabilityCalculator__inner-card'>
      Mortgage affordability calculator
      <div>
        <form className='MortgageAffordabilityCalculator__inner-container' onSubmit={handleSubmit}>
          <div className='MortgageAffordabilityCalculator__question-container'>
            <div>
              {Object.values(applicants).map((applicant: Applicant, index) => (
                <ApplicantDetails
                  key={applicant.id}
                  annualIncome={applicant.annualIncome}
                  otherIncome={applicant.otherIncome}
                  error={errorRule(applicant)}
                  firstApplicant={index === 0}
                  updateApplicant={(annualIncome: number, otherIncome: number) =>
                    handleUpdateApplicant(annualIncome, otherIncome, applicant.id)
                  }
                />
              ))}
              {Object.keys(applicants).length < 2 && (
                <div className='MortgageAffordabilityCalculator__button-padding'>
                  <Button variant='secondary' type='button' onClick={() => handleNewApplicant()}>
                    Add additional applicant
                  </Button>
                </div>
              )}
            </div>
            <div className='MortgageAffordabilityCalculator__input-container'>
              <label htmlFor='deposit'>How much deposit do you have?</label>
              <div className='MortgageAffordabilityCalculator__property-comment'>
                You'll usually need at least 5% of the property's value
              </div>
              <Input
                name='deposit'
                value={deposit}
                onChange={(e) => setDeposit(e.target.value)}
                prefix='£'
                format='thousands'
                error={errorDeposit}
              />
              {errorDeposit && (
                <div className='MortgageAffordabilityCalculator__error'>
                  Min deposit amount £5,000, max £10,000,000
                </div>
              )}
            </div>
          </div>
          <div className='MortgageAffordabilityCalculator__footer-container'>
            <Button
              withIcon
              variant='primary'
              type='submit'
              disabled={applicantErrors || errorDeposit}
            >
              Calculate
            </Button>
            <div className='MortgageAffordabilityCalculator__mojo-logo-affordability'>
              <Icon glyph='powered-by-mojo' />
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

const MortgageAffordabilityCalculator = () => {
  const [showMortgageGauge, setShowMortgageGauge] = useState(false)
  const [loanDetails, setLoanDetails] = useState<LoanDetails>({
    estPropertyBudget: 539000,
    borrowing: 300000,
    loanToValue: 3.1,
    deposit: 6000,
  })

  const handleFormSubmit = (calculation) => {
    setLoanDetails(calculation)
    setShowMortgageGauge(true)
  }

  return (
    <div className='MortgageAffordabilityCalculator'>
      <script
        type='text/javascript'
        src='//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js'
        async
      />
      <div className='MortgageAffordabilityCalculator__gauge-card MortgageAffordabilityCalculator__main-content-container'>
        <div className='MortgageAffordabilityCalculator__content-container'>
          <div className='MortgageAffordabilityCalculator__left-side'>
            <h1 className='MortgageAffordabilityCalculator__heading'>How much can I borrow?</h1>
            <p className='MortgageAffordabilityCalculator__paragraph'>
              Use our online mortgage calculator to get a rough estimate of how much you could
              borrow to buy your own home.
            </p>
          </div>
          {showMortgageGauge ? (
            <ResultsScreen loanDetails={loanDetails} setShowMortgageGauge={setShowMortgageGauge} />
          ) : (
            <ApplicantForm onSubmit={handleFormSubmit} />
          )}
        </div>
      </div>
    </div>
  )
}

export default MortgageAffordabilityCalculator
